import { useState } from 'preact/hooks';
import ProductImageContainer from './ProductImageContainer';
import ProductTitle from './ProductTitle';
import ProductLocation from './ProductLocation';
import ProductReview from './ProductReview';
import ProductAttributes from './ProductAttributes';
import ProductPricing from './ProductPricing';
import OutOfStockOverlay from './OutOfStockOverlay';
import { Product } from '../types';

interface ProductCardProps {
  product: Product;
}

/**
 * ProductCard - Main product card component with hover interactions
 * 
 * **Hover Behavior:**
 * - `Desktop`: Hover over card to initialize image carousel
 * - `Mobile`: Carousel initializes when card enters viewport
 * - Image scales up by 1.05 on card hover
 * - Swiper navigation arrows appear on hover
 * 
 * **Features:**
 * - Responsive design with equal height cards
 * - Image carousel with pagination
 * - Product information display
 * - Price and discount handling
 * 
 * @param product - Product data object
 * @returns JSX element
 */
const ProductCard = ({ product }: ProductCardProps) => {
  const [isCardHovered, setIsCardHovered] = useState(false);
  
  const handleCardMouseEnter = () => { setIsCardHovered(true); };
  const handleCardMouseLeave = () => { setIsCardHovered(false); };

  return (
    <article 
      className={`group bg-white rounded-3xl shadow-lg outline outline-1 outline-offset-[-1px] outline-gray-100 overflow-hidden flex flex-col h-full relative ${!product.isAvailable ? 'product-card-out-of-stock' : ''}`}
      onMouseEnter={product.isAvailable ? handleCardMouseEnter : undefined}
      onMouseLeave={product.isAvailable ? handleCardMouseLeave : undefined}
    >
      {!product.isAvailable && <OutOfStockOverlay />}
      <a 
          href={`/products/${product.handle}`}
          aria-label={`View details for ${product.title}`}
          className="no-underline text-inherit hover:no-underline flex flex-col h-full"
        >
     
        <ProductImageContainer 
          images={product.images} 
          title={product.title} 
          isCardHovered={product.isAvailable ? isCardHovered : false}
          isNotInteractive={!product.isAvailable}
          uspTags={product.uspTags}
        />
        
      <div
                className="p-4 md:!p-5 flex flex-col gap-4 md:!gap-5 flex-1 no-underline text-inherit hover:no-underline"
>
          <header className="flex flex-col gap-3 flex-1">
            <ProductTitle title={product.title} />
            
            <div className="inline-flex items-center gap-3 w-full">
              <div className="max-w-2/3">
                <ProductLocation location={product.location} />
              </div>
              <div className="w-auto">
                <ProductReview rating={product.rating} reviews={product.reviews} />
              </div>
            </div>
            
            <ProductAttributes attributes={product.attributes} />
          </header>
          
          <span className="w-full border-t border-gray-200 !block"></span>
          
          <ProductPricing 
            price={product.price} 
            originalPrice={product.originalPrice} 
            discount={product.discount} 
          />
          </div>
        </a>
      </article>
    );
};

export default ProductCard; 